import { memo } from 'react';

import { DownloadIcon, Link2Icon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { IconButton } from '@air/primitive-icon-button';
import { Tooltip } from '@air/primitive-tooltip';

import { AgentDropdownMenu } from '~/components/v2/components/private-selection-bar/agent-selection-bar/agent-dropdown-menu/AgentDropdownMenu';
import { renderAgentDropdownItems } from '~/components/v2/components/private-selection-bar/agent-selection-bar/agent-dropdown-menu/renderAgentDropdownItems';
import { PrivateSelectionBarShowcaseShareMenu } from '~/components/v2/components/private-selection-bar/PrivateSelectionBarShowcaseShareMenu';
import { type SelectionBarActions } from '~/components/v2/components/shared/selectionBar/selectionBarActions';
import { getSelectionBarShareActionLabel } from '~/components/v2/components/shared/selectionBar/utils/getSelectionBarShareActionLabel';

export type AgentSelectionBarCtaActionsProps = {
  actions: SelectionBarActions;
  /** Icon-only outline CTAs — keeps Download/Share visible when bar is narrow. */
  compactOutlineCtAs?: boolean;
};

type AgentShareButtonProps = {
  share: NonNullable<SelectionBarActions['share']>;
  compact: boolean;
};

const AgentShareButton = memo(({ share, compact }: AgentShareButtonProps) => {
  if (share.kind === 'collection' && share.collectionItemCount === undefined) {
    return null;
  }

  const label = getSelectionBarShareActionLabel(share);
  const tooltipLabel = share.kind === 'collection' ? 'Add to showcase' : label;
  const trigger = compact ? (
    <IconButton
      adsIcon
      appearance="ghost"
      className="shrink-0 rounded-6 border ads:border-ads-white/20 border-white/20 bg-transparent ads:text-inverse text-white hover:ads:bg-ads-white/10 hover:bg-white/10 hover:ads:text-inverse hover:text-white active:ads:bg-ads-white/15 active:bg-white/15"
      data-testid="SELECTION_ACTION_BAR_SHARE"
      icon={Link2Icon}
      label={label}
      onClick={share.kind === 'collection' ? undefined : share.onSelect}
      size="medium"
    />
  ) : (
    <Button
      appearance="outline"
      className="shrink-0 rounded-6 ads:border-ads-white/20 border-white/20 bg-transparent ads:text-inverse text-white hover:ads:bg-ads-white/10 hover:bg-white/10 hover:ads:text-inverse hover:text-white active:ads:bg-ads-white/15 active:bg-white/15"
      color="gray"
      data-testid="SELECTION_ACTION_BAR_SHARE"
      onClick={share.kind === 'collection' ? undefined : share.onSelect}
      prefix={<Link2Icon size="sm" aria-hidden="true" />}
      size="medium"
    >
      {label}
    </Button>
  );

  if (share.kind === 'collection') {
    return (
      <PrivateSelectionBarShowcaseShareMenu
        menu={AgentDropdownMenu}
        onCreateShowcase={share.onSelect}
        renderItems={renderAgentDropdownItems}
        trigger={trigger}
        triggerTooltip={tooltipLabel}
      />
    );
  }

  return <Tooltip label={tooltipLabel}>{trigger}</Tooltip>;
});

AgentShareButton.displayName = 'AgentShareButton';

/**
 * The agent bar's calls to action. Outlined rather than filled so they sit on the glass instead of
 * punching through it, and full height so they carry the weight the icon actions deliberately don't.
 */
export const AgentSelectionBarCtaActions = memo(
  ({ actions, compactOutlineCtAs = false }: AgentSelectionBarCtaActionsProps) => (
    <div className="flex items-center gap-2">
      {actions.insert && (
        <Button
          appearance="outline"
          color="gray"
          className="shrink-0 rounded-6 ads:border-ads-white/20 border-white/20 bg-transparent ads:text-inverse text-white hover:ads:bg-ads-white/10 hover:bg-white/10 hover:ads:text-inverse hover:text-white active:ads:bg-ads-white/15 active:bg-white/15"
          data-testid="SELECTION_ACTION_BAR_INSERT"
          onClick={actions.insert.onSelect}
          size="medium"
        >
          {actions.insert.count !== undefined ? `Insert ${actions.insert.count}` : 'Insert'}
        </Button>
      )}

      {actions.download && (
        <Tooltip label={actions.download.hasMultiple ? 'Download all' : 'Download'}>
          <IconButton
            adsIcon
            appearance="ghost"
            className="shrink-0 rounded-6 border ads:border-ads-white/20 border-white/20 bg-transparent ads:text-inverse text-white hover:ads:bg-ads-white/10 hover:bg-white/10 hover:ads:text-inverse hover:text-white active:ads:bg-ads-white/15 active:bg-white/15"
            data-testid="SELECTION_ACTION_BAR_DOWNLOAD"
            label={actions.download.hasMultiple ? 'Download all' : 'Download'}
            onClick={actions.download.onSelect}
            size="medium"
            icon={DownloadIcon}
          />
        </Tooltip>
      )}

      {actions.share && <AgentShareButton compact={compactOutlineCtAs} share={actions.share} />}
    </div>
  ),
);

AgentSelectionBarCtaActions.displayName = 'AgentSelectionBarCtaActions';
